import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { PageShell, SectionCard } from "../components/ui";
import { API_URL } from "../utils/runtimeConfig";
import zombieBoardMap from "../assets/infinite-zombie/board-map.jpg";
import "./css/InfiniteZombieShare.css";

const TOWER_IMAGES = {
  machineGun: "/infinite-zombie/towers/machine-gun-direct.jpg?v=2",
  rocket: "/infinite-zombie/towers/rocket-direct.jpg?v=2",
  emp: "/infinite-zombie/towers/emp-direct.jpg?v=2",
  laser: "/infinite-zombie/towers/laser-direct.jpg?v=2",
};

const ITEM_META = {
  orange_ore: {
    label: "Resources (2400)",
    icon: "/infinite-zombie/items/orange_ore.png?v=4",
  },
  orange_emp: {
    label: "Damage +30%",
    icon: "/infinite-zombie/items/orange_emp.png?v=4",
  },
  orange_rocket: {
    label: "A-Bomb",
    icon: "/infinite-zombie/items/orange_rocket.png?v=4",
  },
  orange_laser: {
    label: "Weather Controller",
    icon: "/infinite-zombie/items/orange_laser.png?v=4",
  },

  purple_ore: {
    label: "Resource (1200)",
    icon: "/infinite-zombie/items/purple_ore.png?v=4",
  },
  purple_ore_up: {
    label: "Resource Rate +12%",
    icon: "/infinite-zombie/items/purple_ore_up.png?v=4",
  },
  purple_hp: {
    label: "HP (40)",
    icon: "/infinite-zombie/items/purple_hp.png?v=4",
  },
  purple_rocket: {
    label: "Rocket Damage +40%",
    icon: "/infinite-zombie/items/purple_rocket.png?v=4",
  },
  purple_laser: {
    label: "Laser Damage +40%",
    icon: "/infinite-zombie/items/purple_laser.png?v=4",
  },
  purple_emp: {
    label: "Damage +15%",
    icon: "/infinite-zombie/items/purple_emp.png?v=4",
  },
  purple_engineer: {
    label: "Freezing Robot",
    icon: "/infinite-zombie/items/purple_engineer.png?v=4",
  },

  blue_ore: {
    label: "Resources (600)",
    icon: "/infinite-zombie/items/blue_ore.png?v=4",
  },
  blue_ore_up: {
    label: "Resource Rate +6%",
    icon: "/infinite-zombie/items/blue_ore_up.png?v=4",
  },
  blue_hp: {
    label: "HP (20)",
    icon: "/infinite-zombie/items/blue_hp.png?v=4",
  },
  blue_rocket: {
    label: "Machine Gun Damage +20%",
    icon: "/infinite-zombie/items/blue_rocket.png?v=4",
  },
  blue_laser: {
    label: "Laser Damage +20%",
    icon: "/infinite-zombie/items/blue_laser.png?v=4",
  },
  blue_emp: {
    label: "EMP Damage +20%",
    icon: "/infinite-zombie/items/blue_emp.png?v=4",
  },
  blue_gift: {
    label: "Reward +1",
    icon: "/infinite-zombie/items/blue_gift.png?v=4",
  },
  blue_part: {
    label: "Reward Quality Boost",
    icon: "/infinite-zombie/items/blue_part.png?v=4",
  },
  blue_engineer: {
    label: "Energize",
    icon: "/infinite-zombie/items/blue_engineer.png?v=4",
  },

  green_ore: {
    label: "Resources (300)",
    icon: "/infinite-zombie/items/green_ore.png?v=4",
  },
  green_ore_up: {
    label: "Resource Rate +3%",
    icon: "/infinite-zombie/items/green_ore_up.png?v=4",
  },
  green_hp: {
    label: "HP (10)",
    icon: "/infinite-zombie/items/green_hp.png?v=4",
  },
  green_engineer: {
    label: "Explosive Robot",
    icon: "/infinite-zombie/items/green_engineer.png?v=4",
  },
};

const ItemPill = ({ value }) => {
  const meta = ITEM_META[value || ""];
  if (!meta) return <span className="iz-item-pill-empty">No pick</span>;
  return (
    <span className="iz-item-pill">
      <img src={meta.icon} alt={meta.label} />
      <span>{meta.label}</span>
    </span>
  );
};

const InfiniteZombieShare = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(
          `${API_URL}/events/public/${encodeURIComponent(token || "")}`,
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json?.message || "Invalid share link");
        setData(json);
      } catch (e) {
        setError(e.message || "Failed to load share");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  const plannerPlugin = String(data?.plannerPlugin || "");
  const plannerPayload = useMemo(
    () => ((data?.payload && typeof data.payload === "object") ? data.payload : {}),
    [data?.payload],
  );
  const isInfiniteZombieShare = plannerPlugin === "infiniteZombiePlanner";
  const isTriangleShare = plannerPlugin === "triangleStrongholdPlanner";

  const waveItems = useMemo(
    () => plannerPayload?.waveItems || [],
    [plannerPayload],
  );

  if (loading) {
    return (
      <PageShell maxWidth="lg">
        <SectionCard>
          <Typography>Loading shared plan…</Typography>
        </SectionCard>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell maxWidth="lg">
        <SectionCard>
          <Typography color="error">{error}</Typography>
        </SectionCard>
      </PageShell>
    );
  }

  if (isInfiniteZombieShare) {
    return (
      <PageShell maxWidth="xl">
        <SectionCard className="iz-share-page" sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography variant="h5" className="iz-share-title">
            Infinite Zombies Share
          </Typography>
          <Typography variant="body2" className="iz-share-sub">
            {data?.eventMeta?.displayLabel || "Plan"}
          </Typography>

          <Box className="iz-share-layout">
            <div className="iz-board-wrap">
              <div
                className="iz-board"
                style={{ backgroundImage: `url(${zombieBoardMap})` }}
              >
                {(plannerPayload?.boardSlots || []).map((slot) => (
                  <div
                    key={slot.slotId}
                    className="iz-slot"
                    style={{
                      left: `${slot.x}%`,
                      top: `${slot.y}%`,
                      background: slot.towerType
                        ? "transparent"
                        : "radial-gradient(circle at 30% 30%, #60a5fa, #1d4ed8)",
                    }}
                  >
                    {slot.towerType && (
                      <img
                        src={TOWER_IMAGES[slot.towerType]}
                        alt={slot.towerType}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="iz-panel">
              <h3>Start Picks</h3>
              {["orange", "purple", "blue", "green"].map((tier) => (
                <div key={tier} className="iz-item-row">
                  <div className="iz-tier-label">{tier}</div>
                  <ItemPill value={plannerPayload?.startItems?.[tier]} />
                </div>
              ))}

              <h3 style={{ marginTop: 14 }}>Wave Picks</h3>
              <div className="iz-wave-list">
                {waveItems.map((w) => (
                  <div key={w.wave} className="iz-wave-row">
                    <div className="iz-wave-label">Wave {w.wave}</div>
                    <ItemPill value={w.item} />
                  </div>
                ))}
              </div>
            </div>
          </Box>
        </SectionCard>
      </PageShell>
    );
  }

  if (isTriangleShare) {
    const islands = Array.isArray(plannerPayload?.islands) ? plannerPayload.islands : [];
    const strongholds = Array.isArray(plannerPayload?.strongholds) ? plannerPayload.strongholds : [];
    const byIslandId = new Map(
      islands.map((island) => [String(island?._id || ""), { island, slots: [] }]),
    );
    strongholds.forEach((slot) => {
      const key = String(slot?.islandId || "");
      if (!byIslandId.has(key)) {
        byIslandId.set(key, { island: { _id: key, name: key }, slots: [] });
      }
      byIslandId.get(key).slots.push(slot);
    });
    const islandsOrdered = Array.from(byIslandId.values());

    return (
      <PageShell maxWidth="xl">
        <SectionCard sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography variant="h5" className="iz-share-title">
            Triangle/DoD Share
          </Typography>
          <Typography variant="body2" className="iz-share-sub">
            {data?.eventMeta?.displayLabel || "Planner Share"}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.2, mb: 2 }}>
            Defenders: {(strongholds || []).filter((slot) => !!slot?.defendingCommander).length}/
            {Number(plannerPayload?.maxDefenders || 0)}
          </Typography>

          {islandsOrdered.map(({ island, slots }) => (
            <SectionCard key={String(island?._id || island?.name)} sx={{ p: 1.5, mb: 1.25 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {island?.name || "Island"}
              </Typography>
              <div className="iz-wave-list">
                {slots
                  .slice()
                  .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")))
                  .map((slot) => {
                    const commanderId = String(slot?.defendingCommander || "");
                    const ownerAllianceId = String(slot?.ownerAlliance || "");
                    return (
                      <div key={String(slot?._id || slot?.name)} className="iz-wave-row">
                        <div className="iz-wave-label">{slot?.name || "Stronghold"}</div>
                        <div>
                          {commanderId
                            ? (data?.lookup?.commanders?.[commanderId] || commanderId)
                            : "Unassigned"}
                          {ownerAllianceId
                            ? ` · ${data?.lookup?.alliances?.[ownerAllianceId] || ownerAllianceId}`
                            : ""}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </SectionCard>
          ))}
        </SectionCard>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="lg">
      <SectionCard>
        <Typography variant="h6">Shared Planner</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {data?.eventMeta?.displayLabel || "Planner Share"} ({plannerPlugin || "unknown"})
        </Typography>
        <pre style={{ marginTop: 12, whiteSpace: "pre-wrap" }}>
          {JSON.stringify(plannerPayload || {}, null, 2)}
        </pre>
      </SectionCard>
    </PageShell>
  );
};

export default InfiniteZombieShare;
